import memoizeOne from 'memoize-one';
import React, { Component, Fragment } from 'react';
import { ExFactoryReferencePrice, Item } from '~/Api/User/ProductGroup/AeMaxPrice';
import { DebounceInput } from '~/Debounce';
import FormattedProductPrice from '~/FormattedProductPrice';
import { ProductPrice } from '~/Product';
import ProductStatusIcon from '~/ProductStatusIcon';
import { Link } from '~/UI/Button';
import Checkbox from '~/UI/Checkbox/Checkbox';
import CountryIcon from '~/UI/CountryIcon/CountryIcon';
import inputCompactStyles from '~/UI/Input/compact.less';
import Input from '~/UI/Input/Input';
import defaultSegmentedSelectStyles from '~/UI/SegmentedSelect/default.less';
import SegmentedSelectOption from '~/UI/SegmentedSelect/Option/SegmentedSelectOption';
import SegmentedSelectPage from '~/UI/SegmentedSelect/Page/SegmentedSelectPage';
import SegmentedSelectSegment from '~/UI/SegmentedSelect/Segment/SegmentedSelectSegment';
import SegmentedSelect from '~/UI/SegmentedSelect/SegmentedSelect';

import styles from './styles.less';

export interface Values {
	packageSizeForSingleUnit: { value: string; isValid: boolean | null };
	exFactoryPrice: {
		value: { value: string | null; parentValue: null; prompt: React.ReactNode };
		isValid: boolean;
	};
	selected: boolean;
}

export type OnChangeListener = (
	item: Item,
	packageSizeForSingleUnit: string,
	exFactoryPrice: {
		value: string | null;
		parentValue: null;
		prompt: React.ReactNode;
	}
) => void;
export type OnSelectListener = (item: Item, selected: boolean, range: boolean) => void;

interface Props {
	item: Item;
	price: ProductPrice | null;
	values: Values;
	disableSelection: boolean;
	highlightProduct: boolean;
	lastSelectedProduct: boolean;
	detailLink(product: { id: string }): { pathname: string; search: string };
	style: any;
	onChange: OnChangeListener;
	onSelect: OnSelectListener;
	onSave(): void;
	checked: boolean;
	checkRow(value: boolean): void;
}

export default class Row extends Component<Props> {
	static defaultProps = {
		disableSelection: false,
		highlightProduct: false,
		lastSelectedProduct: false,
		style: null,
	};

	shouldComponentUpdate(nextProps: Props) {
		return (
			JSON.stringify(this.props.item) !== JSON.stringify(nextProps.item) ||
			JSON.stringify(this.props.price) !== JSON.stringify(nextProps.price) ||
			this.props.values.packageSizeForSingleUnit.isValid !== nextProps.values.packageSizeForSingleUnit.isValid ||
			this.props.values.packageSizeForSingleUnit.value !== nextProps.values.packageSizeForSingleUnit.value ||
			this.props.values.exFactoryPrice.isValid !== nextProps.values.exFactoryPrice.isValid ||
			this.props.values.exFactoryPrice.value?.value !== nextProps.values.exFactoryPrice.value?.value ||
			this.props.values.selected !== nextProps.values.selected ||
			this.props.disableSelection !== nextProps.disableSelection ||
			this.props.highlightProduct !== nextProps.highlightProduct ||
			this.props.lastSelectedProduct !== nextProps.lastSelectedProduct ||
			this.props.style !== nextProps.style ||
			this.props.onChange !== nextProps.onChange ||
			this.props.onSelect !== nextProps.onSelect ||
			this.props.checked !== nextProps.checked
		);
	}

	resolveExFactoryPrice = memoizeOne((type: string | null, item: Item): ExFactoryReferencePrice | null => {
		if (type === null) {
			return null;
		}
		return item.referencePrices.exFactory.find((exFactory) => exFactory.calculatedPrice.type === type) ?? null;
	});

	render() {
		const {
			item,
			price,
			values,
			disableSelection,
			highlightProduct,
			lastSelectedProduct,
			detailLink,
			style,
			checked,
			onChange,
			onSave,
			checkRow,
		} = this.props;
		const { product } = item;
		const detailLinkString = detailLink(product);

		return (
			<div
				className={
					product.status.isRemoved() || product.status.isBeforeIntroduction()
						? styles.removedRow
						: highlightProduct
						? styles.highlightedRow
						: styles.row
				}
				data-testid='row'
				style={style}
			>
				<div className={styles.checkboxContainer} data-testid='delete-product'>
					<Checkbox checked={checked} onChange={checkRow} />
				</div>
				<div className={styles.countryCell}>
					<Link styles={{ button: styles.countryCellContent }} tabIndex={-1} to={detailLinkString}>
						<div className={styles.countryIcon}>
							<CountryIcon code={product.dataset.source.country.alpha2} name={product.dataset.source.country.name} />
						</div>
						{product.dataset.source.country.alpha2}
						<abbr
							className={styles.datasetSourceShortName}
							title={`${product.dataset.source.shortName} — ${product.dataset.source.description}`}
						>
							{product.dataset.source.shortName}
						</abbr>
					</Link>
				</div>
				<div className={styles.brandCell}>
					<Link styles={{ button: styles.brandCellContent }} tabIndex={-1} to={detailLinkString}>
						{product.uppercasedBrandName}
					</Link>
				</div>
				<div className={styles.mahCell}>
					<Link styles={{ button: styles.mahCellContent }} tabIndex={-1} to={detailLinkString}>
						{product.uppercasedMarketingAuthorizationHolder}
					</Link>
				</div>
				<div className={styles.dosageCell}>
					<Link styles={{ button: styles.dosageCellContent }} tabIndex={-1} to={detailLinkString}>
						{product.uppercasedDosageForm}
					</Link>
				</div>
				<div className={styles.dosageStrengthCell}>
					<Link styles={{ button: styles.dosageStrengthCellContent }} tabIndex={-1} to={detailLinkString}>
						{product.dosageStrength}
					</Link>
				</div>
				<div className={styles.packageCell}>
					{item.singleUnitPackageSize ? (
						<div className={styles.packageInput}>
							<DebounceInput
								value={values.packageSizeForSingleUnit.value}
								onBlur={onSave}
								onChange={(value) => onChange(item, value, values.exFactoryPrice.value)}
							>
								<Input
									id={`product-package-${product.id}`}
									isInvalid={values.packageSizeForSingleUnit.isValid === false}
									styles={inputCompactStyles}
								/>
							</DebounceInput>
						</div>
					) : (
						<Link styles={{ button: styles.packageCellContent }} tabIndex={-1} to={detailLinkString}>
							{product.packageSize}
						</Link>
					)}
				</div>
				<div className={styles.exFactoryPriceCell}>{this.renderExFactoryPriceSelect()}</div>
				<div className={styles.publicPriceCell}>
					<Link styles={{ button: styles.priceCellContent }} tabIndex={-1} to={detailLinkString}>
						<div>{item.referencePrices.public?.sourcePrice.price?.roundedHumanFormat()}</div>
						<div>
							<strong>{item.referencePrices.public?.convertedPrice.roundedHumanFormat()}</strong>
						</div>
					</Link>
				</div>
				<div className={styles.priceCell} data-testid='cell-price'>
					<Link styles={{ button: styles.priceCellContent }} tabIndex={-1} to={detailLinkString}>
						<FormattedProductPrice
							rounded
							dataSource={product.dataset.source}
							isFetching={false}
							productPrice={price}
						/>
					</Link>
				</div>
				<div className={styles.priceTypeCell}>
					<Link styles={{ button: styles.priceTypeCellContent }} tabIndex={-1} to={detailLinkString}>
						{price?.humanCondensedFormatType()}
					</Link>
				</div>
				<div className={styles.selectionCell}>
					<ProductStatusIcon status={product.status} />
					<Checkbox
						disabled={disableSelection}
						focused={lastSelectedProduct}
						checked={!disableSelection && values.selected}
						id={`product-selection-${product.id}`}
						onChange={this.selectionChanged}
					/>
				</div>
			</div>
		);
	}

	renderExFactoryPriceSelect() {
		const { item, values, onChange } = this.props;

		if (item.referencePrices.exFactory.length === 0) {
			return null;
		}

		const exFactoryPrice = this.resolveExFactoryPrice(values.exFactoryPrice.value.value, item);

		return (
			<SegmentedSelect
				isValid={values.exFactoryPrice.isValid}
				prompt={
					exFactoryPrice ? (
						this.renderReferencePrice(exFactoryPrice, false)
					) : (
						<div className={styles.emptySelection}>{item.allowsEmptyCalculator.exFactory ? 'No price' : '\xA0'}</div>
					)
				}
				selectedOption={values.exFactoryPrice.value}
				styles={defaultSegmentedSelectStyles}
				onSelect={(value) => {
					if (value) {
						onChange(item, values.packageSizeForSingleUnit.value, value);
					}
				}}
			>
				<SegmentedSelectPage>
					<SegmentedSelectSegment>
						{item.allowsEmptyCalculator.exFactory ? (
							<SegmentedSelectOption prompt={null} value={null}>
								No price
							</SegmentedSelectOption>
						) : null}

						{item.referencePrices.exFactory.map((referencePrice) => (
							<SegmentedSelectOption
								key={referencePrice.calculatedPrice.type}
								prompt={this.renderReferencePrice(referencePrice, false)}
								value={referencePrice.calculatedPrice.type}
							>
								{this.renderReferencePrice(referencePrice, true)}
							</SegmentedSelectOption>
						))}
					</SegmentedSelectSegment>
				</SegmentedSelectPage>
			</SegmentedSelect>
		);
	}

	renderReferencePrice(referencePrice: ExFactoryReferencePrice, includeDescription: boolean) {
		return (
			<Fragment>
				{includeDescription ? <div>{referencePrice.calculatedPrice.description}</div> : null}
				<div>
					{referencePrice.sourcePrice.price?.roundedHumanFormat()}
					{includeDescription ? (
						<Fragment>&nbsp;{referencePrice.sourcePrice.humanCondensedFormatType()}</Fragment>
					) : null}
				</div>
				<div>
					<strong>{referencePrice.calculatedPrice.targetCurrencyPrice.roundedHumanFormat()}</strong>
				</div>
			</Fragment>
		);
	}

	selectionChanged = (checked: boolean, event: React.MouseEvent<HTMLLabelElement>) => {
		const { item, onSelect } = this.props;
		onSelect(item, checked, event.nativeEvent.shiftKey);
	};
}
